import { CalendarCheck, MapPin, Menu, Phone, X } from "lucide-react";
import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatedRoutes } from "./AnimatedRoutes";
import { ChatWidget } from "./ChatWidget";
import { EmailSignup } from "./EmailSignup";
import { HeritageOrnament } from "./HeritageOrnament";
import { useAuth } from "../context/AuthContext";
import { siteInfo } from "../data/site";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/apartments", label: "Apartments" },
  { to: "/gallery", label: "Gallery" },
  { to: "/local-guide", label: "Local Guide" },
  { to: "/contact", label: "Contact" },
];

export function Layout() {
  const [open, setOpen] = useState(false);
  const { account } = useAuth();
  const location = useLocation();
  const chatHiddenRoutes = ["/account", "/privacy", "/unsubscribe"];
  const showChatWidget = !location.pathname.startsWith("/admin") && !chatHiddenRoutes.includes(location.pathname);

  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="top-bar">
          <div className="top-bar__inner">
            <a href={siteInfo.mapUrl} target="_blank" rel="noreferrer">
              {siteInfo.address}
            </a>
            <span className="est-mark top-bar__est">Est. 1915</span>
            <a href={siteInfo.phoneHref}>
              <Phone size={15} aria-hidden="true" />
              {siteInfo.phone}
            </a>
          </div>
        </div>
        <div className="nav-bar">
          <div className="nav-bar__brand">
            <NavLink className="brand" to="/" onClick={() => setOpen(false)}>
              <img src="/assets/brand/logo-square-300.jpg" alt="" />
              <span>
                <strong>{siteInfo.name}</strong>
                <small>{siteInfo.shortTagline}</small>
              </span>
            </NavLink>
          </div>
          <div className="nav-bar__menu">
            <button className="icon-button nav-toggle" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label="Toggle navigation">
              {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
            <nav className={open ? "site-nav site-nav--open" : "site-nav"} aria-label="Primary navigation">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className={({ isActive }) => (isActive ? "active" : undefined)}>
                  {item.label}
                </NavLink>
              ))}
              <NavLink to="/account" onClick={() => setOpen(false)} className={({ isActive }) => (isActive ? "active" : undefined)}>
                {account ? "My stays" : "Account"}
              </NavLink>
              <a className="button button--nav" href="/#availability" onClick={() => setOpen(false)}>
                Book
              </a>
            </nav>
          </div>
        </div>
      </header>
      <main>
        <AnimatedRoutes />
      </main>
      {showChatWidget && <ChatWidget />}
      <EmailSignup />
      <footer className="site-footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <img src="/assets/brand/logo-square-300.jpg" alt="" className="footer-logo" />
            <h2>{siteInfo.name}</h2>
            <p>{siteInfo.tagline}</p>
            <span className="est-mark">Est. 1915</span>
            <div className="footer-mobile-actions" aria-label="Footer quick actions">
              <a href="/#availability">
                <CalendarCheck size={17} aria-hidden="true" />
                Book
              </a>
              <a href={siteInfo.phoneHref}>
                <Phone size={17} aria-hidden="true" />
                Call
              </a>
              <a href={siteInfo.mapUrl} target="_blank" rel="noreferrer">
                <MapPin size={17} aria-hidden="true" />
                Map
              </a>
            </div>
          </div>
          <div className="footer-section footer-section--visit">
            <h3>Visit</h3>
            <p>{siteInfo.address}</p>
            <p>
              Check-in: {siteInfo.checkIn}
              <br />
              Check-out: {siteInfo.checkOut}
            </p>
          </div>
          <div className="footer-section footer-section--contact">
            <h3>Contact</h3>
            <p>
              <a href={siteInfo.phoneHref}>{siteInfo.phone}</a>
              <br />
              <a href={siteInfo.youtubeUrl} target="_blank" rel="noreferrer">
                Video tour
              </a>
              <br />
              <a href={siteInfo.mapUrl} target="_blank" rel="noreferrer">
                Map and directions
              </a>
            </p>
          </div>
          <div className="footer-section footer-section--pages">
            <h3>Pages</h3>
            <p>
              <NavLink to="/apartments">Apartments</NavLink>
              <br />
              <NavLink to="/contact">Contact</NavLink>
              <br />
              <NavLink to="/account">Account</NavLink>
              <br />
              <NavLink to="/privacy">Privacy</NavLink>
            </p>
          </div>
        </div>
        <HeritageOrnament className="footer-ornament" />
        <div className="footer-bottom">
          <span className="footer-colophon">The Deposit Seed House</span>
          <span>Restored 1915 — Deposit, New York</span>
        </div>
      </footer>
    </div>
  );
}
